// Hard-coded native-reporting layouts. Kept in sync with the backend chart
// registry (src/main/reporting/common/report-registry.ts). The layout is static
// metadata (which charts, in what order), so when native reporting is on we skip
// the GET /reporting/:reportKey/layout round-trip and render from here. Each
// chart's DATA still comes from POST /reporting/:reportKey/chart/:chartKey.

export interface ReportLayoutChart {
  key: string;
  title: string;
  visualisationType: string;
  width: number;
  // Extra chartify formData for this chart, e.g. { bar_stacked: true } or axis titles.
  formData?: { [key: string]: string | boolean };
}

// Free-text filters a report's charts actually understand. The backend ignores
// any filter its charts don't wire up, so declaring them per report keeps the
// filter bar from showing controls that silently do nothing (e.g. "Inquiry Id"
// on the Booking report, whose cards key off the booking table).
export interface ReportTextFilter {
  key: string;
  placeholder: string;
}

export interface ReportLayout {
  reportKey: string;
  title: string;
  // Filter-bar preset to open on (a RANGE_OPTIONS key); falls back to the global default.
  defaultRange?: string;
  charts: ReportLayoutChart[];
  textFilters: ReportTextFilter[];
}

export const NATIVE_REPORT_LAYOUTS: { [reportKey: string]: ReportLayout } = {
  inquiry: {
    reportKey: 'inquiry',
    title: 'Inquiry Reports',
    textFilters: [
      { key: 'internalReferenceNumber', placeholder: 'Internal Ref. No.' },
      { key: 'inquiryReferenceNumber', placeholder: 'Inquiry Number' },
    ],
    charts: [
      { key: 'bid_level_details', title: 'Bid Level Details', visualisationType: 'table', width: 12 },
      { key: 'inquiry_level_details', title: 'Inquiry Level Details', visualisationType: 'table', width: 12 },
      { key: 'bids_detail_view', title: 'Bids Detail View', visualisationType: 'table', width: 12 },
      { key: 'avg_max_vs_min_cost', title: 'Average Max v/s Average Min Cost', visualisationType: 'table', width: 12 },
      { key: 'vendor_participation', title: 'Vendor Participation', visualisationType: 'table', width: 12 },
      { key: 'inquiry_lcl_fcl', title: 'Inquiry LCL FCL Reports', visualisationType: 'table', width: 12 },
      { key: 'inquiry_savings', title: 'Inquiry Savings Report', visualisationType: 'table', width: 12 },
    ],
  },
  booking: {
    reportKey: 'booking',
    title: 'Booking Reports',
    // The Booking cards filter the `booking` table, so they expose Booking No.
    // and the internal reference held in booking_details — not the inquiry id.
    textFilters: [
      { key: 'internalReferenceNumber', placeholder: 'Internal Ref. No.' },
      { key: 'bookingNumber', placeholder: 'Booking No.' },
    ],
    charts: [
      { key: 'booking_details', title: 'Booking Details', visualisationType: 'table', width: 12 },
      { key: 'bid_level_booking_details', title: 'Bid Level Booking Details', visualisationType: 'table', width: 12 },
    ],
  },
  shipment: {
    reportKey: 'shipment',
    title: 'Shipment Reports',
    // Only the Activity Log card exposed a Shipment Number filter in Metabase; the
    // date range applies to every card via the shipment creation date.
    textFilters: [{ key: 'shipmentNumber', placeholder: 'Shipment Number' }],
    charts: [
      { key: 'total_shipments', title: 'Total Shipments', visualisationType: 'scalar', width: 6 },
      { key: 'containers_moved', title: 'Containers Moved', visualisationType: 'scalar', width: 6 },
      { key: 'trade_type_bifurcation', title: 'Trade Type Bifurcation', visualisationType: 'bar', width: 6 },
      { key: 'mode_wise_bifurcation', title: 'Mode Wise Bifurcation', visualisationType: 'bar', width: 6 },
      { key: 'milestone_wise_bifurcation', title: 'Milestone Wise Bifurcation', visualisationType: 'bar', width: 6 },
      { key: 'container_size_distribution', title: 'Container Size Distribution', visualisationType: 'bar', width: 6 },
      { key: 'task_status_distribution', title: 'Task Status Distribution', visualisationType: 'bar', width: 6 },
      { key: 'shipment_trend', title: 'Shipment Trend', visualisationType: 'line', width: 12 },
      { key: 'carrier_distribution', title: 'Carrier Distribution', visualisationType: 'table', width: 4 },
      { key: 'ff_distribution', title: 'FF Distribution', visualisationType: 'table', width: 4 },
      { key: 'users_active', title: 'Users Active', visualisationType: 'table', width: 4 },
      { key: 'activity_log', title: 'Activity Log across Shipments', visualisationType: 'table', width: 12 },
      { key: 'tasks_in_active_shipments', title: 'Tasks in Active Shipments', visualisationType: 'table', width: 12 },
      { key: 'tasks_approaching_deadline', title: 'Tasks approaching Deadline', visualisationType: 'table', width: 12 },
      { key: 'tasks_delayed', title: 'Tasks Delayed', visualisationType: 'table', width: 12 },
      { key: 'comment_log', title: 'Comment Log across Shipments', visualisationType: 'table', width: 12 },
      { key: 'do_changes', title: 'Changes in DOs across Shipments', visualisationType: 'table', width: 12 },
    ],
  },
  air_cargo: {
    reportKey: 'air_cargo',
    title: 'Air Cargo Reports',
    // The Air Analytics cards take no text filters; the date range applies to every
    // card via the HAWB creation date.
    textFilters: [],
    charts: [
      {
        key: 'delay_distribution',
        title: 'Delay Distribution in Shipments',
        visualisationType: 'bar',
        width: 6,
        formData: { x_axis_label: 'Delay Time - In hours', y_axis_label: 'Number of Shipments' },
      },
      { key: 'status_distribution', title: 'Status wise distribution of active cargo', visualisationType: 'pie', width: 6 },
      { key: 'vendor_pallet_distribution', title: 'Vendor wise pallet distribution', visualisationType: 'area', width: 6 },
      {
        key: 'vendor_delay_distribution',
        title: 'Vendor wise delay distribution',
        visualisationType: 'bar',
        width: 6,
        formData: { bar_stacked: true, x_axis_label: 'Vendor' },
      },
    ],
  },
  rfq: {
    reportKey: 'rfq',
    title: 'RFQ Reports',
    // RFQ plans are created a few dozen a month; the global 1-week default is near-empty.
    defaultRange: '6m',
    textFilters: [],
    charts: [
      { key: 'rfq_volume_trend', title: 'RFQ Volume Trend', visualisationType: 'bar', width: 6, formData: { bar_stacked: true } },
      {
        key: 'rfq_approval_pipeline',
        title: 'Approval Pipeline',
        visualisationType: 'bar',
        width: 6,
        formData: { bar_type: 'horizontal' },
      },
      { key: 'rfq_vendor_scorecard', title: 'Vendor Scorecard', visualisationType: 'table', width: 12 },
      { key: 'rfq_lane_rate_benchmark', title: 'Lane Rate Benchmark', visualisationType: 'table', width: 12 },
      { key: 'rfq_activity_log', title: 'RFQ Activity Log', visualisationType: 'table', width: 12 },
    ],
  },
};
